import React, { useMemo, useState, useEffect } from 'react';
import { Button, FormGroup, FormControl, ControlLabel, Checkbox, Row, Col } from 'react-bootstrap';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Spinner from '../../../../../../../components/Loaders/Spinner';
import Summernote from '../../../../../../../components/Summernote';
import SelectOptions from './SelectOptions';

import { summernoteOptions } from '../../../../helpers';

const OPTION_TYPES = ['checkbox', 'input', 'course', 'file', 'multi_input', 'select', 'textarea', 'meta_tag_editable', 'amounts'];

const FieldsOptions = ({
  access,
  item,
  formNode,
  isLoadingSubmit,
  isLoadingData,
  error,
  amountsType,
  showData,
  onChangeOptAmount,
  onShowData,
  onSubmitForm,
}) => {
  const [isSubmited, setIsSubmited] = useState(false);
  const [multiInputs, setMultiInputs] = useState([]);
  const [multiAmounts, setMultiAmounts] = useState([]);

  const options = useMemo(
    () => item?.details?.options?.filter((opt) => OPTION_TYPES.includes(opt.type)) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item?.details?.options],
  );

  const onEditMetaTag = (option) => {
    const tagInput = formNode.current[option?.name];
    if (tagInput) {
      if (tagInput?.hasAttribute('disabled')) {
        tagInput.disabled = false;
        tagInput.removeAttribute('disabled');
      } else {
        setIsSubmited(true);
        onSubmitForm(undefined, false);
      }
    }
  };

  const onAddMultiInput = (e, code, type) => {
    e.preventDefault();
    switch (type) {
    case 'multi_input':
      setMultiInputs((prevState) => [...prevState, { id: code + prevState.length + 1, value: '' }]);
      break;

    case 'amounts':
      setMultiAmounts((prevState) => [...prevState, { id: code + prevState.length + 1, value: { amount: '', description: '' } }]);
      break;

    default:
      break;
    }
  };

  const onRemoveMultiInput = (id, type) => {
    switch (type) {
    case 'multi_input':
      setMultiInputs((prevState) => prevState.filter((item) => item.id !== id));
      break;

    case 'amounts':
      setMultiAmounts((prevState) => prevState.filter((item) => item.id !== id));
      break;

    default:
      break;
    }
  };

  useEffect(() => {
    const findMulti = options?.find((opt) => opt.type === 'multi_input');
    if (findMulti) {
      setMultiInputs(findMulti?.values?.map((value, index) => ({ id: findMulti?.code + index, value })));
    }
    const findAmounts = options?.find((opt) => opt.type === 'amounts');
    if (findAmounts) {
      setMultiAmounts(findAmounts?.values?.map((value, index) => ({ id: findAmounts?.type + index, value })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, options]);

  return (
    <>
      {options?.length ? (
        <div className="payments-edit__options">
          {access?.payments_get ? (
            <div
              className={classNames('show-data', {
                'd-none': showData,
              })}
            >
              <FormGroup>
                <ControlLabel>Show data</ControlLabel>
                <FormControl type="hidden" name="show_data" defaultValue />
              </FormGroup>
              <Button bsStyle="primary" onClick={() => onShowData()} disabled={isLoadingData || isLoadingSubmit}>
                {isLoadingData || isLoadingSubmit ? <Spinner /> : null}
                <span>Show data</span>
              </Button>
            </div>
          ) : null}
          {options?.map((opt) => {
            switch (opt.type) {
            case 'checkbox':
              return (
                <FormGroup key={opt.code}>
                  <Checkbox id={opt.code} name={`options[${opt.name}]`} defaultChecked={Number(opt.value)} defaultValue>
                    <span className="fw-700">{opt.label}</span>
                  </Checkbox>
                </FormGroup>
              );

            case 'input':
            case 'course':
              return (
                <FormGroup key={opt.code || opt?.hmac_secret}>
                  <ControlLabel htmlFor={opt.code || opt?.hmac_secret}>{opt.label}</ControlLabel>
                  <FormControl
                    type="text"
                    id={opt.code || opt?.hmac_secret}
                    name={`options[${opt.name}]`}
                    placeholder={opt.placeholder?.replace(/&quot;/g, '"') || ''}
                    defaultValue={opt?.value || ''}
                    disabled={opt?.disabled}
                  />
                </FormGroup>
              );

            case 'file':
              return (
                <FormGroup key={opt.code}>
                  <ControlLabel htmlFor={opt.code}>{opt.label}</ControlLabel>
                  <FormControl type="hidden" name={`files[${opt.name}]`} defaultValue={opt?.file_name || opt?.name || ''} />
                  <FormControl type={opt.type} id={opt.code} name={`${opt.name}`} accept={opt.accept} disabled={opt?.disabled} />
                </FormGroup>
              );

            case 'multi_input':
              return (
                <div key={opt.code} className={classNames('', { hidden: amountsType === '1' })}>
                  {multiInputs?.map((value, index) => (
                    <FormGroup key={value.id} className="group-multi-input">
                      <span className="fas fa-times remove-group" onClick={() => onRemoveMultiInput(value.id, opt.type)} />
                      <ControlLabel htmlFor={value.id}>{opt.label}</ControlLabel>
                      <FormControl
                        type="text"
                        id={value.id}
                        name={`options[${opt.name}][${index}]`}
                        placeholder={opt.placeholder?.replace(/&quot;/g, '"') || ''}
                        defaultValue={value.value || ''}
                        disabled={opt?.disabled}
                        required={amountsType === '0'}
                      />
                    </FormGroup>
                  ))}
                  <FormGroup>
                    <a href="# " onClick={(e) => onAddMultiInput(e, opt.code, opt.type)}>
                      <span>{opt.code === 'descriptions' ? 'Add descriptions' : 'Add'}</span>
                    </a>
                  </FormGroup>
                </div>
              );

            case 'textarea':
              if (opt?.options?.class === 'raw-textarea') {
                return (
                  <FormGroup key={opt.code}>
                    <ControlLabel htmlFor={opt.code}>{opt.label}</ControlLabel>
                    <FormControl
                      componentClass="textarea"
                      id={opt.code}
                      name={`options[${opt.name}]`}
                      placeholder={opt.placeholder?.replace(/&quot;/g, '"') || ''}
                      rows={10}
                      cols={30}
                      defaultValue={opt?.value || ''}
                      disabled={opt?.disabled}
                    />
                  </FormGroup>
                );
              }
              return (
                <FormGroup key={opt.code}>
                  <ControlLabel htmlFor={opt.code}>{opt.label}</ControlLabel>
                  <Summernote value={opt?.value || ''} name={`options[${opt.name}]`} options={summernoteOptions} />
                </FormGroup>
              );

            case 'select':
              return (
                <FormGroup key={opt.code}>
                  <ControlLabel htmlFor={opt.code}>{opt.label}</ControlLabel>
                  <FormControl
                    id={opt.code}
                    name={`options[${opt.name}]`}
                    componentClass="select"
                    placeholder={opt.placeholder}
                    defaultValue={opt.value}
                    onChange={opt.name === 'amount' ? onChangeOptAmount : undefined}
                  >
                    <SelectOptions options={opt?.options} />
                  </FormControl>
                </FormGroup>
              );

            case 'meta_tag_editable':
              return (
                <FormGroup key={opt.code}>
                  <ControlLabel htmlFor={opt.code}>{opt.label}</ControlLabel>
                  <div className="d-flex align-center">
                    <FormControl
                      type="text"
                      id={opt.code}
                      name={`options[${opt.name}]`}
                      placeholder={opt.placeholder?.replace(/&quot;/g, '"') || ''}
                      defaultValue={opt?.value || ''}
                      disabled={(!!opt?.value || isSubmited || isLoadingSubmit) && !error}
                    />
                    <Button className="ml-15" disabled={(isLoadingSubmit || isSubmited) && !error} onClick={() => onEditMetaTag(opt)}>
                      {isLoadingSubmit && <Spinner />}
                      <span>{opt?.value ? 'Edit' : 'Add'}</span>
                    </Button>
                  </div>
                </FormGroup>
              );

            case 'amounts':
              return (
                <div key={opt.code} className={classNames('', { hidden: amountsType === '0' })}>
                  {multiAmounts?.map((amount, index) => (
                    <FormGroup key={amount.id} className="group-multi-amounts">
                      <span className="fas fa-times remove-group" onClick={() => onRemoveMultiInput(amount.id, opt.type)} />
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <ControlLabel htmlFor={amount.id + opt.label.amount}>{opt.label.amount}</ControlLabel>
                            <FormControl
                              type="number"
                              id={amount.id}
                              name={`options[${opt.type}][${opt.name.amount}][${index}]`}
                              placeholder={opt.placeholder?.replace(/&quot;/g, '"') || ''}
                              defaultValue={amount.value.amount || ''}
                              disabled={opt?.disabled}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={8}>
                          <FormGroup>
                            <ControlLabel htmlFor={amount.id + opt.label.description}>{opt.label.description}</ControlLabel>
                            <FormControl
                              type="text"
                              id={amount.id}
                              name={`options[${opt.type}][${opt.name.description}][${index}]`}
                              placeholder={opt.placeholder?.replace(/&quot;/g, '"') || ''}
                              defaultValue={amount.value.description || ''}
                              disabled={opt?.disabled}
                              required={amountsType === '1'}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </FormGroup>
                  ))}
                  <FormGroup>
                    <a href="# " className="group-multi-amounts__add" onClick={(e) => onAddMultiInput(e, opt.code, opt.type)}>
                      <span>Add option</span>
                    </a>
                  </FormGroup>
                </div>
              );

            default:
              return null;
            }
          })}
        </div>
      ) : null}
    </>
  );
};

FieldsOptions.propTypes = {
  access: PropTypes.object,
  item: PropTypes.object,
  formNode: PropTypes.object,
  error: PropTypes.string,
  isLoadingSubmit: PropTypes.bool,
  isLoadingData: PropTypes.bool,
  amountsType: PropTypes.string,
  showData: PropTypes.bool,
  onChangeOptAmount: PropTypes.func,
  onShowData: PropTypes.func,
  onSubmitForm: PropTypes.func,
};

export default FieldsOptions;
